import React, { useState, useEffect, useMemo } from 'react';
import { Reorder } from 'framer-motion';
import { DraggableTab } from './DraggableTab';
import { HydratedPost, UserData, Achievement, DailyAchievements, PostType, Post, CompletedAchievement, PageState } from '../types';
import { useScrollLock } from '../hooks/useScrollLock';
import { PostGrid } from './PostGrid';
import { FootprintPostCreatorModal } from './PostCreator';
import {
    TrophyIcon,
    FilterIcon,
    XIcon,
    EditIcon,
    BookmarkIcon,
    CloseIcon,
    SettingsIcon,
    BellIcon,
    SendIcon
} from './Icons';
import { Leaderboards } from './Leaderboards';
import { stickerTextStyle } from './StickerText';
import { getFootprintCycleDate } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import { updateUserOnServer, sendSystemBroadcast } from '../api';
import { useUpload } from '../contexts/UploadContext';
import { Share } from '@capacitor/share';

interface FootprintPageProps {
    posts: HydratedPost[];
    failedPosts?: HydratedPost[];
    onPostClick: (post: HydratedPost) => void;
    onLike: (postId: number) => void;
    onBump: (post: HydratedPost) => void;
    onBookmark: (postId: number) => void;
    onEdit: (post: HydratedPost) => void;
    onDelete: (postId: number) => void;
    onNavigateToHashtag: (tag: string) => void;
    onNavigateToCommunity?: (id: string, channelId?: string) => void;
    animatingLikes: Set<number>;
    animatingBumps: Set<number>;
    animatingBookmarks: Set<number>;
    galleryAnimation: string;
    postBlurOpacity: number;
    gridColumns?: 1 | 2 | 3 | 4 | 5 | 6;
    onGridColumnsChange?: (cols: 1 | 2 | 3 | 4 | 5 | 6) => void;
    preferredScale?: number;
    onSwipeTransition?: (direction: 'left' | 'right') => void;
    onScaleChange?: (scale: number) => void;
    isModalOpen?: boolean;
    currentUserData: UserData;
    currentUserId: string;
    onCompleteAchievement: (achievement: Achievement, postData: {
        type: PostType;
        content: Post["content"];
        visibility?: "public" | "followers" | "groups" | "private";
        visibilityGroups?: string[];
        showCaption?: boolean;
        showOnProfile?: boolean;
        textSize?: 'xs' | 'sm' | 'med' | 'lg';
        communityId?: string;
        themeOpacity?: number;
        isOptimistic?: boolean;
        localId?: number;
        fontId?: string;
        postcardStyle?: string;
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => void;
    allUsers: Record<string, UserData>;
    dailyAchievements: DailyAchievements;
    onOpenAdminSettings: () => void;
    isAdmin: boolean;
    onNavigateToProfile: (userId: string) => void;
    onVouch?: (postId: number) => void;
    onMuteUser?: (userId: string) => void;
    onBlockUser?: (userId: string) => void;
    onGrantAdmin?: (userId: string) => void;
    onDeleteUser?: (userId: string) => void;
    onReportPost?: (postId: number) => void;
    setPage: (page: PageState) => void;
    achievements: Achievement[];
    taskId?: number;
    isDemo?: boolean;
    onShare?: (post: HydratedPost, slideIndex?: number) => void;
    params?: any;
    initialVisibleCount?: number;
    onVisibleCountChange?: (count: number) => void;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    isLoading?: boolean;
    onUploadFailed?: (localId: number) => void;
    onRetryPostUpload?: (localId: number, startUpload: any) => void;
    tabsOrder?: string[];
    onReorderTabs?: (newOrder: string[]) => void;
    isShakingMode?: boolean;
    postcardGap?: number;
    onPostcardGapChange?: (gap: number) => void;
}

export const FootprintPage: React.FC<FootprintPageProps> = ({
    posts,
    failedPosts = [],
    onPostClick,
    onLike,
    onBump,
    onBookmark,
    onEdit,
    onDelete,
    onNavigateToHashtag,
    animatingLikes,
    animatingBumps,
    animatingBookmarks,
    galleryAnimation,
    postBlurOpacity,
    gridColumns = 1,
    onGridColumnsChange,
    preferredScale = 1,
    onScaleChange,
    onSwipeTransition,
    isModalOpen = false,
    currentUserData,
    currentUserId,
    onCompleteAchievement,
    allUsers,
    dailyAchievements,
    onOpenAdminSettings,
    isAdmin,
    onNavigateToProfile,
    onVouch,
    onMuteUser,
    onBlockUser,
    onGrantAdmin,
    onDeleteUser,
    onReportPost,
    onNavigateToCommunity,
    setPage,
    achievements,
    taskId,
    isDemo = false,
    onShare,
    initialVisibleCount,
    onVisibleCountChange,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    isLoading,
    params,
    onUploadFailed,
    onRetryPostUpload,
    tabsOrder,
    onReorderTabs,
    isShakingMode = false,
    postcardGap = 8,
    onPostcardGapChange
}: FootprintPageProps) => {
    console.log('[Footprint Debug] FootprintPage rendered. achievements:', achievements);

    const { setUsersData, footprintFeedMode: footTab, setFootprintFeedMode: setFootTab } = useAuth();
    const { startUpload } = useUpload();
    const isAllowedToEdit = isAdmin || currentUserId === 'goodlyfe' || currentUserId === 'agk';

    const [taskToComplete, setTaskToComplete] = useState<Achievement | null>(null);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

    const currentFootprintStmt = allUsers['goodlyfe']?.footprintStatement || "Get 3 Vouches to get your points!\nEach Vouch (like) is worth 1pt! Make sure you vouch valid posts! We spread love on this app, don't be stingy!";
    const [footprintStmtInput, setFootprintStmtInput] = useState(currentFootprintStmt);

    useEffect(() => {
        setFootprintStmtInput(currentFootprintStmt);
    }, [currentFootprintStmt]);

    useScrollLock(!!taskToComplete);

    useEffect(() => {
        onInternalModalStateChange?.(!!taskToComplete);
    }, [!!taskToComplete, onInternalModalStateChange]);

    useEffect(() => {
        if (taskId) {
            setFootTab('all');
        }
    }, [taskId]);

    const allCompletions = useMemo(() => {
        const list: Array<{ userId: string; achievementId: number; date: string; postId: number; status: string }> = [];
        Object.entries(allUsers).forEach(([userId, user]) => {
            (user.completedAchievements || []).forEach(c => {
                list.push({ userId, achievementId: c.achievementId, date: c.date, postId: c.postId, status: c.status });
            });
        });
        return list.sort((a, b) => b.date.localeCompare(a.date));
    }, [allUsers]);

    useEffect(() => {
        if (params?.tab) {
            setFootTab(params.tab);
        }
    }, [params?.tab, setFootTab]);

    const todayStr = getFootprintCycleDate();
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAgoStr = weekAgo.toISOString().slice(0, 10);

    const connectionsList = useMemo(() => {
        const cur = currentUserData;
        if (!cur) return [] as typeof allCompletions;
        const set = new Set<string>();
        (cur.following || []).forEach(u => set.add(u));
        (cur.followers || []).forEach(u => set.add(u));
        return allCompletions.filter(c => set.has(c.userId));
    }, [allCompletions, currentUserData]);

    const userPosts = useMemo(() => {
        const combined = [...failedPosts, ...posts.filter(p => !failedPosts.some(fp => fp.id === p.id || (fp as any).localId === (p as any).localId))];
        let p = combined.filter(post => post.footprintDetails);
        if (taskId) {
            p = p.filter(post => post.footprintDetails?.achievementId === taskId);
        }
        return p;
    }, [posts, failedPosts, taskId]);

    const getPostCycleDate = (createdAtStr: string | undefined): string => {
        if (!createdAtStr) return '';
        try {
            const date = new Date(createdAtStr);
            const etStr = date.toLocaleString("en-US", { timeZone: "America/New_York" });
            const etDate = new Date(etStr);
            const hour = etDate.getHours();

            const cycleDate = new Date(etDate);
            if (hour < 12) {
                cycleDate.setDate(cycleDate.getDate() - 1);
            }

            return cycleDate.getFullYear() + '-' +
                String(cycleDate.getMonth() + 1).padStart(2, '0') + '-' +
                String(cycleDate.getDate()).padStart(2, '0');
        } catch (e) {
            return '';
        }
    };

    const filteredPosts = useMemo(() => {
        console.log('[Footprint Debug] Filtering posts. Tab:', footTab);
        console.log('[Footprint Debug] todayStr:', todayStr);
        console.log('[Footprint Debug] weekAgoStr:', weekAgoStr);
        console.log('[Footprint Debug] userPosts count:', userPosts.length);

        let result = userPosts;
        switch (footTab) {
            case 'today':
                result = userPosts.filter(p => getPostCycleDate(p.createdAt) === todayStr || p.uploadFailed);
                break;
            case 'week':
                result = userPosts.filter(p => getPostCycleDate(p.createdAt) >= weekAgoStr || p.uploadFailed);
                break;
            case 'connections': {
                const cur = currentUserData;
                if (!cur) {
                    result = [];
                    break;
                }
                const set = new Set<string>();
                (cur.following || []).forEach(u => set.add(u.toLowerCase()));
                (cur.followers || []).forEach(u => set.add(u.toLowerCase()));
                result = userPosts.filter(p => set.has(p.author.toLowerCase()) || p.uploadFailed);
                break;
            }
            case 'all':
            default:
                result = userPosts;
                break;
        }

        const sorted = [...result].sort((a, b) => {
            if (a.uploadFailed && !b.uploadFailed) return -1;
            if (!a.uploadFailed && b.uploadFailed) return 1;
            return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
        });

        console.log('[Footprint Debug] Filtered posts count:', sorted.length);
        return sorted;
    }, [footTab, userPosts, currentUserData, todayStr, weekAgoStr]);

    const today = getFootprintCycleDate();
    const completedTodayAchievement = useMemo(() => {
        console.log('[Footprint Debug] Finding completed achievement for date:', today);
        console.log('[Footprint Debug] Current user completedAchievements:', currentUserData?.completedAchievements);
        if (!currentUserData?.completedAchievements) return undefined;

        const foundCompletion = currentUserData.completedAchievements.find(a =>
            a.date === today && Number(a.achievementId) === Number(dailyAchievements?.daily?.id)
        );

        if (!foundCompletion) return undefined;

        const postExists = posts.some(p =>
            !p.isArchived &&
            p.author === currentUserId &&
            p.footprintDetails &&
            Number(p.footprintDetails.achievementId) === Number(dailyAchievements?.daily?.id)
        );

        return postExists ? foundCompletion : undefined;
    }, [currentUserData?.completedAchievements, today, dailyAchievements?.daily?.id, posts, currentUserId]);

    const AchievementCard = ({ achievement, onCompleteClick, completedTodayAchievement }: { achievement: Achievement, onCompleteClick: (achievement: Achievement) => void, completedTodayAchievement: CompletedAchievement | undefined }) => {
        if (!achievement) return null;

        const completedList = currentUserData?.completedAchievements || [];
        const isThisCompleted = completedList.some((c: any) => Number(c.achievementId) === Number(achievement.id)) || (completedTodayAchievement && Number(completedTodayAchievement.achievementId) === Number(achievement.id));
        const isAnotherCompleted = !!completedTodayAchievement && !isThisCompleted;

        const appThemeColor = currentUserData?.appThemeColor || '#10b981';
        const appTextColor = currentUserData?.appTextColor || '#ffffff';
        const themeOpacity = currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50;

        const hexToRgba = (hex: string, op: number) => {
            const hexColor = hex.replace('#', '');
            let r = 0, g = 0, b = 0;
            if (hexColor.length === 3) {
                r = parseInt(hexColor.charAt(0) + hexColor.charAt(0), 16);
                g = parseInt(hexColor.charAt(1) + hexColor.charAt(1), 16);
                b = parseInt(hexColor.charAt(2) + hexColor.charAt(2), 16);
            } else if (hexColor.length === 6) {
                r = parseInt(hexColor.substring(0, 2), 16);
                g = parseInt(hexColor.substring(2, 4), 16);
                b = parseInt(hexColor.substring(4, 6), 16);
            } else return hex;
            return `rgba(${r}, ${g}, ${b}, ${op / 100})`;
        };

        const bgColor = hexToRgba(appThemeColor, themeOpacity);
        const borderColor = hexToRgba(appThemeColor, Math.min(100, themeOpacity + 40));

        const displayPoints = dailyAchievements?.daily?.id === achievement.id
            ? achievement.points
            : Math.round(achievement.points * 0.2);

        return (
            <div className={`p-6 rounded-2xl border flex flex-col`} style={{ backgroundColor: bgColor, borderColor: borderColor }}>
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-xl font-bold mt-1 text-raised" style={{ color: appTextColor, ...stickerTextStyle(0.18) }}>{achievement.title}</h3>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-lg font-bold text-raised" style={{ color: appTextColor, ...stickerTextStyle(0.18) }}>
                            {displayPoints} pts
                        </div>
                        <button
                            onClick={async (e) => {
                                e.stopPropagation();
                                const shareData = {
                                    title: 'GoodLyfe Footprint Task',
                                    text: `Check out this task on GoodLyfe: ${achievement.title} (${displayPoints} pts) - ${achievement.description}`,
                                    url: `https://goodlyfe.net/app?page=footprint&taskId=${achievement.id}`
                                };

                                try {
                                    await Share.share(shareData);
                                } catch (err) {
                                    console.error('Capacitor share failed, trying fallback', err);
                                    if (navigator.share) {
                                        navigator.share(shareData).catch(console.error);
                                    } else if (navigator.clipboard && navigator.clipboard.writeText) {
                                        navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
                                        alert('Link copied to clipboard!');
                                    } else {
                                        const textArea = document.createElement("textarea");
                                        textArea.value = `${shareData.text}\n${shareData.url}`;
                                        document.body.appendChild(textArea);
                                        textArea.focus();
                                        textArea.select();
                                        try {
                                            document.execCommand('copy');
                                            alert('Link copied to clipboard!');
                                        } catch (e) {
                                            console.error('Fallback: Oops, unable to copy', e);
                                            alert('Unable to copy link to clipboard. Share URL: ' + shareData.url);
                                        }
                                        document.body.removeChild(textArea);
                                    }
                                }
                            }}
                            className="p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors flex-shrink-0"
                            aria-label="Share Task"
                        >
                            <SendIcon className="w-5 h-5" style={{ color: appTextColor }} />
                        </button>
                        {isAdmin && (
                            <button
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Send push notification broadcast for task: ${achievement.title}?`)) {
                                        try {
                                            const text = `Daily Footprint Task: ${achievement.title} (${displayPoints} pts) - ${achievement.description}`;
                                            await sendSystemBroadcast(currentUserId, text);
                                            alert('Broadcast sent successfully!');
                                        } catch (error) {
                                            console.error('Failed to send broadcast', error);
                                            alert('Failed to send broadcast.');
                                        }
                                    }
                                }}
                                className="p-1.5 rounded-full bg-blue-600/20 hover:bg-blue-600/40 transition-colors flex-shrink-0"
                                aria-label="Broadcast Task"
                            >
                                <BellIcon className="w-5 h-5 text-blue-500" />
                            </button>
                        )}
                    </div>
                </div>
                <p className="mt-2 flex-grow text-raised" style={{ color: appTextColor, opacity: 0.8, ...stickerTextStyle(0.18) }}>{achievement.description}</p>
                <button
                    onClick={() => {
                        if (isDemo) {
                            alert("Completing achievements is disabled in Play Around mode. Log in to earn points!");
                            return;
                        }
                        onCompleteClick(achievement);
                    }}
                    disabled={isThisCompleted || isAnotherCompleted}
                    className="mt-4 w-full font-bold py-2 px-6 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-black/20 hover:bg-black/30 border border-white/10"
                    style={{ color: appTextColor, ...stickerTextStyle(0.18) }}
                >
                    {isThisCompleted ? 'Completed!' : isAnotherCompleted ? 'Daily Task Done' : 'Complete Task'}
                </button>
            </div>
        );
    };

    const handleCompleteTask = (achievement: Achievement) => (postData: {
        type: PostType,
        content: Post['content'],
        visibility?: 'public' | 'followers' | 'groups' | 'private',
        visibilityGroups?: string[],
        showCaption?: boolean,
        showOnProfile?: boolean,
        textSize?: 'xs' | 'sm' | 'med' | 'lg',
        communityId?: string,
        themeOpacity?: number,
        isOptimistic?: boolean,
        localId?: number,
        fontId?: string,
        postcardStyle?: string,
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => {
        const completedTasks = currentUserData?.completedAchievements || [];
        const isAlreadyCompleted = completedTasks.some((c: any) => Number(c.achievementId) === Number(achievement.id));
        if (isAlreadyCompleted) {
            alert('You have already completed this footprint task!');
            setTaskToComplete(null);
            return;
        }
        onCompleteAchievement(achievement, postData);
    };

    return (
        <>
        <div className="pb-24 pt-16 flex flex-col h-full">
            <FootprintPostCreatorModal
                isOpen={!!taskToComplete}
                onClose={() => setTaskToComplete(null)}
                achievement={taskToComplete}
                onSubmit={taskToComplete ? handleCompleteTask(taskToComplete) : (() => { })}
                onInternalModalStateChange={onInternalModalStateChange}
                closeInternalModalsTrigger={closeInternalModalsTrigger}
                isTaskOfTheDay={taskToComplete ? dailyAchievements?.daily?.id === taskToComplete.id : false}
                onUploadFailed={onUploadFailed}
                footprintRequirements={taskToComplete?.requiredAttachmentType ? [taskToComplete.requiredAttachmentType] : []}
            />

            <div className="max-w-4xl mx-auto px-4 w-full">
                <div className="text-center mb-8 mt-8 p-6 rounded-2xl" style={{ backgroundColor: 'var(--app-theme-bg)' }}>
                    <div className="flex-grow overflow-y-auto">
                        <h1 className="text-4xl font-bold text-raised" style={stickerTextStyle(0.35)}>Daily Footprint</h1>
                        <div className="mt-4 flex justify-center items-center gap-6">
                            <div className="text-2xl font-bold text-raised text-yellow-400" style={stickerTextStyle(0.18)}>{currentUserData?.totalPoints || 0} Lyfe points</div>
                        </div>
                        <div className="mt-4 flex flex-col items-center justify-center gap-3 max-w-md mx-auto">
                            {isAllowedToEdit ? (
                                <div className="w-full flex flex-col gap-2">
                                    <textarea
                                        value={footprintStmtInput}
                                        onChange={(e) => setFootprintStmtInput(e.target.value)}
                                        className="w-full p-3 bg-black/30 text-white text-xs rounded-xl border border-white/10 focus:outline-none focus:ring-1 focus:ring-blue-500 text-center resize-y"
                                        placeholder="Edit daily footprint statement..."
                                        rows={3}
                                        style={stickerTextStyle(0.18)}
                                    />
                                    {footprintStmtInput !== currentFootprintStmt && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    await updateUserOnServer('goodlyfe', { footprintStatement: footprintStmtInput });
                                                    if (typeof setUsersData === 'function') {
                                                        setUsersData((prev: any) => ({
                                                            ...prev,
                                                            goodlyfe: { ...prev.goodlyfe, footprintStatement: footprintStmtInput }
                                                        }));
                                                    } else {
                                                        window.location.reload();
                                                    }
                                                } catch (e) {
                                                    console.error('Failed to update footprint statement', e);
                                                    alert('Failed to update footprint statement');
                                                }
                                            }}
                                            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-full transition-colors self-center"
                                        >
                                            Save Statement
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <p className='text-xs text-raised text-white whitespace-pre-wrap' style={stickerTextStyle(0.18)}>
                                    {currentFootprintStmt}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-center items-center gap-4 mb-8">
                    <button onClick={() => setFootTab('leaderboard')} className={`flex items-center gap-2 font-semibold py-2 px-5 rounded-full transition-colors ${footTab === 'leaderboard' ? 'bg-yellow-600 text-white' : 'bg-yellow-600/20 text-yellow-500 hover:bg-yellow-600/40'}`} style={stickerTextStyle(0.18)}>
                        <TrophyIcon className="w-5 h-5" />
                        <span>Leaderboard</span>
                    </button>

                    <button onClick={() => setIsArchiveModalOpen(true)} className="flex items-center gap-2 font-semibold py-2 px-5 rounded-full transition-colors bg-blue-600/20 text-blue-500 hover:bg-blue-600/40" style={stickerTextStyle(0.18)}>
                        <BookmarkIcon className="w-5 h-5" />
                        <span>Archive</span>
                    </button>

                    {isAdmin && (
                        <button onClick={onOpenAdminSettings} className="flex items-center justify-center p-2 rounded-full transition-colors bg-blue-600/20 text-blue-500 hover:bg-blue-600/40" aria-label="Admin Settings" style={stickerTextStyle(0.18)}>
                            <SettingsIcon className="w-6 h-6" />
                        </button>
                    )}
                </div>

                <div className="mx-auto md:w-1/2">
                    {dailyAchievements.daily && <AchievementCard achievement={dailyAchievements.daily} onCompleteClick={a => setTaskToComplete(a)} completedTodayAchievement={completedTodayAchievement} />}

                    {taskId && (() => {
                        const filteredTask = achievements.find(a => a.id === taskId);
                        if (!filteredTask) return null;
                        return (
                            <div className="mt-8 border-t border-gray-700 pt-6 animate-fade-in">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2" style={stickerTextStyle(0.18)}>
                                        <FilterIcon className="w-5 h-5 text-blue-400" />
                                        Selected Task
                                    </h2>
                                    <button
                                        onClick={() => setPage({ type: 'footprint' })}
                                        className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 bg-red-500/10 hover:bg-red-500/20 px-3 py-1.5 rounded-full"
                                        style={stickerTextStyle(0.18)}
                                    >
                                        <XIcon className="w-4 h-4" /> Clear Filter
                                    </button>
                                </div>
                                <AchievementCard
                                    achievement={filteredTask}
                                    onCompleteClick={a => {
                                        const completed = currentUserData?.completedAchievements || [];
                                        if (completed.some((c: any) => Number(c.achievementId) === Number(a.id))) {
                                            alert('You have already completed this footprint task!');
                                            return;
                                        }
                                        setTaskToComplete(a);
                                    }}
                                    completedTodayAchievement={completedTodayAchievement}
                                />
                            </div>
                        );
                    })()}
                </div>

                <div className={`w-full px-4 lg:px-[5%] py-2 mt-2 ${isReorderMode ? 'relative z-50' : ''}`}>
                    {isReorderMode && (
                        <div 
                            className="fixed inset-0 z-40 bg-transparent" 
                            onClick={() => setIsReorderMode(false)}
                            onTouchStart={() => setIsReorderMode(false)}
                        />
                    )}
                    <nav
                        className="flex space-x-6 overflow-x-auto no-scrollbar py-1"
                        onTouchStart={(e) => e.stopPropagation()}
                        style={{ touchAction: isReorderMode ? 'none' : 'pan-x' }}
                    >
                        {(() => {
                            const baseTabs = [
                                { id: 'all', name: 'All' },
                                { id: 'today', name: 'Today' },
                                { id: 'week', name: 'This Week' },
                                { id: 'connections', name: currentUserData?.followerNickname || 'Connections' },
                                { id: 'leaderboard', name: 'Leaderboard' }
                            ];

                            let orderedTabs = baseTabs;
                            if (tabsOrder && tabsOrder.length > 0) {
                                orderedTabs = [];
                                const baseTabsMap = new Map(baseTabs.map(t => [t.id, t]));

                                tabsOrder.forEach(id => {
                                    if (baseTabsMap.has(id)) {
                                        orderedTabs.push(baseTabsMap.get(id)!);
                                        baseTabsMap.delete(id);
                                    }
                                });

                                baseTabsMap.forEach(t => orderedTabs.push(t));
                            }

                            return (
                                <Reorder.Group 
                                    axis="x" 
                                    values={orderedTabs.map(t => t.id)} 
                                    onReorder={(newOrder) => onReorderTabs && onReorderTabs(newOrder)} 
                                    className="flex space-x-6 h-full"
                                    style={{ overflow: 'visible' }}
                                >
                                    {orderedTabs.map(tab => (
                                        <DraggableTab
                                            key={tab.id}
                                            item={tab.id}
                                            label={tab.name}
                                            isActive={footTab === tab.id}
                                            onClick={() => setFootTab(tab.id as any)}
                                            color={tab.id === 'leaderboard' ? (footTab === tab.id ? '#eab308' : '#fef08a') : (currentUserData?.appTextColor || '#ffffff')}
                                            isReorderMode={isReorderMode}
                                            onEnterReorderMode={() => setIsReorderMode(true)}
                                            onExitReorderMode={() => setIsReorderMode(false)}
                                        />
                                    ))}
                                </Reorder.Group>
                            );
                        })()}
                    </nav>
                </div>
            </div>

            {footTab === 'leaderboard' ? (
                <div className="max-w-4xl mx-auto px-4 w-full">
                    <Leaderboards
                        usersData={allUsers}
                        currentUserId={currentUserId}
                        onNavigateToProfile={onNavigateToProfile}
                    />
                </div>
            ) : (
                <PostGrid
                    key={footTab}
                    posts={filteredPosts}
                    onPostClick={onPostClick}
                    onLike={onLike}
                    onBump={onBump}
                    onBookmark={onBookmark}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onNavigateToProfile={onNavigateToProfile}
                    onNavigateToHashtag={onNavigateToHashtag}
                    onNavigateToCommunity={onNavigateToCommunity}
                    onVouch={onVouch}
                    onMuteUser={onMuteUser}
                    onBlockUser={onBlockUser}
                    onGrantAdmin={onGrantAdmin}
                    onDeleteUser={onDeleteUser}
                    onReportPost={onReportPost}
                    animatingLikes={animatingLikes}
                    animatingBumps={animatingBumps}
                    animatingBookmarks={animatingBookmarks}
                    galleryAnimation={galleryAnimation}
                    postBlurOpacity={postBlurOpacity}
                    preferredColumns={gridColumns}
                    onColumnsChange={onGridColumnsChange}
                    preferredScale={preferredScale}
                    onScaleChange={onScaleChange}
                    onSwipeTransition={onSwipeTransition}
                    isModalOpen={isModalOpen}
                    showEndMessage={footTab === 'connections'}
                    isDemo={isDemo}
                    onShare={onShare}
                    initialVisibleCount={initialVisibleCount}
                    onVisibleCountChange={onVisibleCountChange}
                    isLoading={isLoading}
                    onRetryPostUpload={onRetryPostUpload}
                    isShakingMode={isShakingMode}
                    postcardGap={postcardGap}
                    onPostcardGapChange={onPostcardGapChange}
                />
            )}
        </div>

        <FootprintArchiveModal
            isOpen={isArchiveModalOpen}
            onClose={() => setIsArchiveModalOpen(false)}
            achievements={achievements}
            dailyAchievements={dailyAchievements}
            onNavigateToAchievement={(taskId) => setPage({ type: 'footprint', taskId })}
        />
        </>
    );
};

export const FootprintArchiveModal = ({
    isOpen,
    onClose,
    dailyAchievements,
    onNavigateToAchievement
}: {
    isOpen: boolean;
    onClose: () => void;
    achievements?: any[];
    dailyAchievements: any;
    onNavigateToAchievement: (id: number) => void;
}) => {
    const [archivedItems, setArchivedItems] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!isOpen) return;
        setIsLoading(true);
        import('axios').then(axios => {
            import('../api').then(({ API_URL }) => {
                const token = localStorage.getItem('token');
                const url = `${API_URL}achievements`;
                axios.default.get(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                    .then(res => {
                        if (res.data) {
                            const items = res.data.filter((ach: any) => ach.isArchived);
                            setArchivedItems(items);
                        }
                    })
                    .catch(err => console.error('[Archive Modal] Failed to fetch:', err))
                    .finally(() => setIsLoading(false));
            });
        });
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]" onClick={onClose}>
            <div className="rounded-2xl shadow-lg p-6 max-w-4xl w-full mx-4 h-[80vh] flex flex-col" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-bold">Footprint Archive</h2>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-grow overflow-y-auto min-h-0 -mx-6 px-6 allow-scroll">
                    <div className="space-y-2">
                        {isLoading ? (
                            <div className="text-center text-gray-400 py-10">Loading archived tasks...</div>
                        ) : archivedItems.length === 0 ? (
                            <div className="text-center text-gray-400 py-10 flex flex-col items-center justify-center space-y-4">
                                <p>No archived tasks yet.</p>
                            </div>
                        ) : (
                            archivedItems.map((ach, index) => {
                                const isDaily = dailyAchievements?.daily?.id === ach.id;
                                const displayPoints = isDaily ? ach.points : Math.round(ach.points * 0.2);
                                return (
                                    <div
                                        key={ach.id || index}
                                        onClick={() => { onNavigateToAchievement(ach.id); onClose(); }}
                                        className={`w-full flex items-center gap-4 p-3 rounded-xl hover:bg-white/10 transition-all cursor-pointer border ${
                                            isDaily 
                                                ? 'bg-blue-600/15 border-blue-500/35 hover:bg-blue-600/25' 
                                                : 'bg-gray-800/40 border-transparent hover:bg-gray-800/60'
                                        }`}
                                    >
                                        <div className="flex-grow min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-bold text-white text-base truncate">{ach.title}</span>
                                                {isDaily && (
                                                    <span className="bg-yellow-500/20 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-yellow-500/30">
                                                        DAILY TASK
                                                    </span>
                                                )}
                                                <span className="bg-gray-500/20 text-gray-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-gray-500/30">
                                                    ARCHIVED
                                                </span>
                                            </div>
                                            {ach.requiredAttachmentType && (
                                                <span className="bg-red-500/25 border border-red-500/30 text-red-400 text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-1 inline-block">
                                                    ⚠️ {ach.requiredAttachmentType.toUpperCase().replace('_', ' ')}
                                                </span>
                                            )}
                                            <p className="text-sm text-gray-400 mt-1 truncate">{ach.description}</p>
                                        </div>

                                        <div className="text-right flex-shrink-0">
                                            <span className="font-bold text-yellow-400 text-base">{displayPoints} pts</span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
